import React from "react";
import { ExternalLink, Crosshair, MapPin } from "lucide-react";
import GMapEmbed from "./GMapEmbed";

/**
 * CoordsInput — keyless Google-Maps-style coordinate picker.
 *
 * Pure UX: two numeric inputs (lat / lng) + live Google Maps Embed preview
 * + "Open Google Maps" helper that lets the user copy coords visually.
 * No API key required (we use the public ?output=embed iframe), no Leaflet,
 * no react-leaflet.
 *
 * Props:
 *   - lat, lng       : current values (number | null)
 *   - onChange({lat, lng}) : called on every valid edit
 *   - lang           : "ar" | "en" (defaults "ar")
 *   - testIdPrefix   : data-testid prefix
 */
export default function CoordsInput({ lat, lng, onChange, lang = "ar", testIdPrefix = "coords" }) {
  const isAr = lang === "ar";
  const numericLat = lat === "" || lat == null || isNaN(Number(lat)) ? null : Number(lat);
  const numericLng = lng === "" || lng == null || isNaN(Number(lng)) ? null : Number(lng);
  const hasCoords = numericLat != null && numericLng != null;

  const update = (next) => {
    const newLat = next.lat ?? lat;
    const newLng = next.lng ?? lng;
    if (typeof onChange === "function") onChange({ lat: newLat, lng: newLng });
  };

  const openGoogleMapsHelper = () => {
    // Open Google Maps centered on existing coords or Doha if empty.
    const url = hasCoords
      ? `https://www.google.com/maps/@${numericLat},${numericLng},17z`
      : "https://www.google.com/maps/@25.286,51.531,12z";
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-3" data-testid={testIdPrefix}>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase text-stone-500 font-semibold tracking-wider block mb-1.5">
            {isAr ? "خط العرض (Lat)" : "Latitude"}
          </label>
          <input
            type="number"
            step="0.000001"
            placeholder="25.286"
            data-testid={`${testIdPrefix}-lat`}
            className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white num-display text-sm focus:border-[var(--mp-maroon,#7A1F3D)] focus:outline-none focus:ring-2 focus:ring-[var(--mp-maroon,#7A1F3D)]/15"
            value={lat ?? ""}
            onChange={(e) => update({ lat: e.target.value })}
          />
          <div className="text-[10px] text-stone-400 mt-1">{isAr ? "من -90 إلى 90" : "Range -90 to 90"}</div>
        </div>
        <div>
          <label className="text-[10px] uppercase text-stone-500 font-semibold tracking-wider block mb-1.5">
            {isAr ? "خط الطول (Lng)" : "Longitude"}
          </label>
          <input
            type="number"
            step="0.000001"
            placeholder="51.531"
            data-testid={`${testIdPrefix}-lng`}
            className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white num-display text-sm focus:border-[var(--mp-maroon,#7A1F3D)] focus:outline-none focus:ring-2 focus:ring-[var(--mp-maroon,#7A1F3D)]/15"
            value={lng ?? ""}
            onChange={(e) => update({ lng: e.target.value })}
          />
          <div className="text-[10px] text-stone-400 mt-1">{isAr ? "من -180 إلى 180" : "Range -180 to 180"}</div>
        </div>
      </div>

      <button
        type="button"
        onClick={openGoogleMapsHelper}
        data-testid={`${testIdPrefix}-open-helper`}
        className="w-full text-xs flex items-center justify-center gap-2 py-2 rounded-md border border-stone-300 bg-stone-50 hover:bg-stone-100 transition"
      >
        <ExternalLink size={12} />
        {isAr
          ? "افتح خرائط جوجل لاختيار الموقع ونسخ الإحداثيات"
          : "Open Google Maps to find & copy coordinates"}
      </button>

      <div>
        <div className="text-[10px] uppercase text-stone-500 font-semibold tracking-wider mb-1.5 flex items-center gap-1.5">
          <Crosshair size={11} />
          {isAr ? "معاينة الموقع" : "Location preview"}
        </div>
        {hasCoords ? (
          <GMapEmbed
            lat={numericLat}
            lng={numericLng}
            zoom={16}
            height="h-[240px]"
            testId={`${testIdPrefix}-preview`}
          />
        ) : (
          <div className="h-[240px] rounded-xl bg-stone-50 border border-dashed border-stone-300 flex flex-col items-center justify-center text-stone-400 text-sm" data-testid={`${testIdPrefix}-preview-empty`}>
            <MapPin size={28} className="mb-2 opacity-50" />
            <div>{isAr ? "أدخل خط العرض والطول لمعاينة الموقع" : "Enter lat & lng to preview"}</div>
          </div>
        )}
      </div>
    </div>
  );
}
